import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import DescriptionList from 'Components/DescriptionList/DescriptionList';
import DescriptionListItem from 'Components/DescriptionList/DescriptionListItem';
import FieldSet from 'Components/FieldSet';
import CheckInput from 'Components/Form/CheckInput';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import TextArea from 'Components/Form/TextArea';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import Link from 'Components/Link/Link';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import PageToolbar from 'Components/Page/Toolbar/PageToolbar';
import PageToolbarButton from 'Components/Page/Toolbar/PageToolbarButton';
import PageToolbarSection from 'Components/Page/Toolbar/PageToolbarSection';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { align, icons, kinds } from 'Helpers/Props';
import { settingValue, titleCase } from '../format';
import { useTdarrLibraries } from '../queries';
import { tdarrCrud, tdarrPost } from '../api';
import LibraryPluginStack from '../components/LibraryPluginStack';

const tabs = [
  ['source', 'Source'], ['cache', 'Transcode Cache'], ['output', 'Output Folder'], ['filters', 'Filters'],
  ['transcode', 'Transcode Options'], ['health-check', 'Health Check'], ['schedule', 'Schedule'], ['skip-list', 'Skiplist'], ['variables', 'Variables']
] as const;

const fields: Record<string, string[]> = {
  source: ['name', 'folder', 'processLibrary', 'processTranscodes', 'processHealthChecks', 'scanOnStart', 'scannerThreadCount', 'folderWatching', 'folderWatchScanInterval', 'useFsEvents', 'ffprobeShowData', 'mediaInfoScan', 'closedCaptionScan', 'isDirectoryLibrary'],
  cache: ['cache'],
  output: ['output', 'folderToFolderConversion', 'folderToFolderConversionDeleteSource', 'folderToFolderRecordHistory', 'copyIfConditionsMet'],
  filters: ['containerFilter', 'filterResolutionsSkip', 'filterCodecsSkip', 'filterContainersSkip', 'filterHardlinked'],
  transcode: ['processTranscodes', 'container', 'processPluginsSequentially', 'pluginIDs', 'preset'],
  'health-check': ['processHealthChecks', 'exifToolScan', 'mediaInfoScan', 'ffprobeShowData', 'closedCaptionScan'],
  schedule: ['scheduledScanFindNew', 'schedule'],
  'skip-list': ['foldersToIgnore', 'foldersToIgnoreCaseInsensitive'],
  variables: ['holdNewFiles', 'holdFor', 'holdForDisplayUnit', 'priority']
};

const booleanFields = new Set(['processLibrary', 'processTranscodes', 'processHealthChecks', 'scanOnStart', 'folderWatching', 'useFsEvents', 'ffprobeShowData', 'mediaInfoScan', 'closedCaptionScan', 'isDirectoryLibrary', 'folderToFolderConversion', 'folderToFolderConversionDeleteSource', 'folderToFolderRecordHistory', 'copyIfConditionsMet', 'filterHardlinked', 'processPluginsSequentially', 'exifToolScan', 'scheduledScanFindNew', 'foldersToIgnoreCaseInsensitive', 'holdNewFiles']);
const numberFields = new Set(['scannerThreadCount', 'folderWatchScanInterval', 'holdFor', 'priority']);
const textareaFields = new Set(['preset', 'foldersToIgnore']);
const readOnlyFields = new Set(['pluginIDs', 'schedule']);
type EditableValue = string | number | boolean;
type ScheduleBlock = { _id: string; checked: boolean };

const scheduleDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];
const scheduleColumns = [
  { name: 'day', label: 'Day / Hour', isVisible: true },
  ...Array.from({ length: 24 }, (_, hour) => ({ name: `hour-${hour}`, label: String(hour).padStart(2, '0'), isVisible: true }))
];

export default function LibraryDetails() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { libraryId, tab = 'source' } = useParams<{ libraryId: string; tab?: string }>();
  const libraries = useTdarrLibraries();
  const library = libraries.data?.find((item) => item._id === libraryId);
  const [draft, setDraft] = useState<Record<string, EditableValue>>({});
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  const editableFields = useMemo(() => (fields[tab] ?? []).filter((field) => !readOnlyFields.has(field)), [tab]);
  useEffect(() => {
    if (!library) return;
    const next: Record<string, EditableValue> = {};
    editableFields.forEach((field) => {
      next[field] = booleanFields.has(field) ? Boolean(library[field]) : numberFields.has(field) ? Number(library[field] ?? 0) : String(library[field] ?? '');
    });
    setDraft(next);
  }, [library, editableFields]);
  useEffect(() => { setMessage(null); }, [libraryId, tab]);
  const save = useMutation({
    mutationFn: async () => {
      if (!library) return;
      const patch = { ...draft };
      const folderChanged = tab === 'source' && String(draft.folder) !== library.folder;
      const watcherChanged = tab === 'source' && Boolean(draft.folderWatching) !== Boolean(library.folderWatching);
      if (folderChanged) patch.folderWatching = false;
      await tdarrCrud({ collection: 'LibrarySettingsJSONDB', mode: 'update', docID: library._id, obj: patch });
      if (folderChanged || watcherChanged) {
        await tdarrPost('/toggle-folder-watch', { data: { auto: false, folder: String(draft.folder ?? library.folder), dbID: library._id, status: folderChanged ? false : Boolean(draft.folderWatching) } });
      }
    },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'LibrarySettingsJSONDB'] }); setMessage({ kind: 'success', text: 'Library settings saved.' }); },
    onError: () => setMessage({ kind: 'danger', text: 'Library settings could not be saved.' })
  });
  const updateSchedule = useMutation({
    mutationFn: async (change: { block: ScheduleBlock } | { start: number; end: number; type: string }) => {
      if (!library) return;
      if ('block' in change) {
        await tdarrPost('/update-schedule-block', { data: { dbID: library._id, ele: change.block._id, status: !change.block.checked } });
      } else {
        await tdarrPost('/toggle-schedule', { data: { dbID: library._id, start: change.start, end: change.end, type: change.type } });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'LibrarySettingsJSONDB'] });
      setMessage({ kind: 'success', text: 'Schedule updated.' });
    },
    onError: () => setMessage({ kind: 'danger', text: 'Schedule could not be updated.' })
  });
  const schedule = Array.isArray(library?.schedule) ? library.schedule as ScheduleBlock[] : [];
  const scheduleById = new Map(schedule.map((block) => [block._id, block]));
  if (!tabs.some(([name]) => name === tab)) return <Redirect to={`/libraries/${libraryId}/source`} />;
  return <PageContent title={library?.name ?? 'Library'}>
    <PageToolbar><PageToolbarSection alignContent={align.LEFT}>
      <PageToolbarButton label="Back" iconName={icons.ARROW_LEFT} onPress={() => history.push('/libraries')} />
      <PageToolbarButton label="Stats" iconName={icons.OVERVIEW} onPress={() => history.push('/stats')} />
      <PageToolbarButton label="Save Changes" iconName={icons.SAVE} isSpinning={save.isPending} isDisabled={!library || !editableFields.length} onPress={() => save.mutate()} />
    </PageToolbarSection></PageToolbar>
    <PageContentBody>
      {libraries.isError && <Alert kind={kinds.DANGER}>Unable to load this library.</Alert>}
      {libraries.isSuccess && !library && <Alert kind={kinds.WARNING}>Library not found.</Alert>}
      {library && <>
        <FieldSet legend={library.name}>
          <nav className="tdarr-tabs" aria-label="Library settings">
            {tabs.map(([name, label]) => <Link key={name} className={tab === name ? 'is-active' : undefined} to={`/libraries/${library._id}/${name}`}>{label}</Link>)}
          </nav>
          {message && <Alert kind={message.kind}>{message.text}</Alert>}
          {editableFields.map((field) => <FormGroup key={field} size="large"><FormLabel name={field}>{titleCase(field)}</FormLabel>
            {booleanFields.has(field) ? <CheckInput name={field} value={Boolean(draft[field])} onChange={({ value }) => setDraft((current) => ({ ...current, [field]: Boolean(value) }))} /> :
              textareaFields.has(field) ? <TextArea name={field} value={String(draft[field] ?? '')} onChange={({ value }) => setDraft((current) => ({ ...current, [field]: value }))} /> :
                <TextInput name={field} type={numberFields.has(field) ? 'number' : 'text'} value={draft[field] as string | number ?? ''} onChange={({ value }) => setDraft((current) => ({ ...current, [field]: numberFields.has(field) ? Number(value) : String(value) }))} />}
          </FormGroup>)}
          {tab !== 'schedule' && tab !== 'transcode' && (fields[tab] ?? []).some((field) => readOnlyFields.has(field)) && <DescriptionList>
            {(fields[tab] ?? []).filter((field) => readOnlyFields.has(field)).map((field) => <DescriptionListItem key={field} title={titleCase(field)} data={settingValue(library[field])} />)}
          </DescriptionList>}
          {tab === 'schedule' && <>
            <Alert>Schedule changes are applied immediately. Use a day, hour, or the Toggle All button to change a complete range.</Alert>
            <Button size="small" isDisabled={updateSchedule.isPending || schedule.length !== 168} onPress={() => updateSchedule.mutate({ start: 0, end: 168, type: '' })}>Toggle All</Button>
            {schedule.length === 168 ? <Table columns={scheduleColumns}>
              <TableBody>
                {scheduleDays.map((day, dayIndex) => <TableRow key={day}>
                  <TableRowCell><Button size="small" isDisabled={updateSchedule.isPending} onPress={() => updateSchedule.mutate({ start: dayIndex * 24, end: (dayIndex + 1) * 24, type: '' })}>{day}</Button></TableRowCell>
                  {Array.from({ length: 24 }, (_, hour) => {
                    const endHour = hour === 23 ? '00' : String(hour + 1).padStart(2, '0');
                    const id = `${day}:${String(hour).padStart(2, '0')}-${endHour}`;
                    const block = scheduleById.get(id);
                    return <TableRowCell key={id}><CheckInput name={id} value={Boolean(block?.checked)} isDisabled={!block || updateSchedule.isPending} onChange={() => block && updateSchedule.mutate({ block })} /></TableRowCell>;
                  })}
                </TableRow>)}
                <TableRow>
                  <TableRowCell>Hour</TableRowCell>
                  {Array.from({ length: 24 }, (_, hour) => <TableRowCell key={hour}><Button size="small" isDisabled={updateSchedule.isPending} onPress={() => updateSchedule.mutate({ start: hour, end: 24, type: 'Hour' })}>{String(hour).padStart(2, '0')}</Button></TableRowCell>)}
                </TableRow>
              </TableBody>
            </Table> : <Alert kind={kinds.WARNING}>This library does not contain a complete 168-hour schedule.</Alert>}
          </>}
          {tab === 'transcode' && <LibraryPluginStack library={library} onMessage={setMessage} />}
        </FieldSet>
      </>}
    </PageContentBody>
  </PageContent>;
}
